<template>
    <div class="container">
        <br />
        <br />

        <!-- Breadcrumbs -->
        <div class="row">
            <div class="col-12">
                <router-link :to="backRoute">Learners</router-link>
                <span class="text-secondary mx-2">/</span>
                <span>{{ learnerUser.email }}</span>
            </div>
        </div>
        <br />

        <!-- Info Block -->
        <div class="row">
            <div class="col-12">
                <h5 class="mb-3">{{ learnerUser.email }}</h5>
                <div class="mb-2">
                    <p class="text-secondary mb-1 small">First access on {{ displayDate(learnerUser.created) }}</p>
                    <p class="text-secondary mb-1 small" v-if="learnerUser.last_lti_launch">Last access on {{ displayDate(learnerUser.last_lti_launch) }}</p>
                </div>
                <p class="small mb-0" v-if="learnerUser.lms_instance">
                    <span class="align-middle">Created by LMS: </span
                    ><span class="badge badge-light align-middle">{{ learnerUser.lms_instance.name }}</span>
                </p>
            </div>
        </div>

        <br />
        <hr />
        <br />

        <!-- Session History Table -->
        <div class="row">
            <div class="col-12">
                <h5 class="mb-3">Recent Sessions</h5>
            </div>
        </div>
        <div class="row">
            <div class="col-12">
                <base-table
                    :table-data="learnerUser.sessions"
                    :config="sessionTableConfig"
                    v-on:activity="showActivity($event)"
                    v-on:responses="showSessionResponses($event)"
                    v-on:download="downloadSummary($event)"
                    empty-message="This user doesn't have any sessions."
                    :rows-per-page="20"
                ></base-table>
            </div>
        </div>

        <br />
        <hr />
        <br />

        <!-- Activity Modal -->
        <base-modal id="activity-modal" :loading="modalLoading">
            <template slot="title"> Session Activity </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <div v-for="activity in session.activities" :key="activity.id">
                            <p class="mb-1 mr-1">Screen: {{ activity.node_name }}</p>
                            <p class="text-secondary mb-0 small">{{ displayDate(activity.timestamp) }}</p>
                            <hr />
                        </div>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
            </template>
        </base-modal>

        <!-- Response Modal -->
        <base-modal id="response-modal" :loading="modalLoading" size="large">
            <template slot="title">
                <div class="mb-1">Session Responses</div>
                <p class="text-secondary tiny mb-0">Session ID: {{ session.id }}</p>
            </template>
            <template>
                <div class="mb-4 mt-2 pb-2 d-flex align-items-center">
                    <div class="flex-grow-1">
                        <div v-if="session.score !== null" class="">
                            <span class="small font-weight-bold">
                                Total Score:
                                <span>
                                    <span>{{ session.score }}</span>
                                    <span v-if="session.score_max">/{{ session.score_max }}</span>
                                </span>
                            </span>
                        </div>
                    </div>
                    <div v-if="session?.results?.responses?.length !== 0" class="flex-grow-0">
                        <button @click="downloadSummary(session)" class="btn btn-sm btn-light">
                            <span class="icon icon-glyphicons-basic-cloud-download mr-1"></span>
                            <span>Response Summary PDF</span>
                        </button>
                    </div>
                </div>
                <learner-results-summary
                    v-if="session"
                    :results="session.results"
                    :display-timestamps="true"
                    :show-chat-logs="true"
                    :show-notes="true"
                    :include-report-feedback="false"
                ></learner-results-summary>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
            </template>
        </base-modal>

        <br />
        <br />
    </div>
</template>

<script>
import http from "@/axios.js"
import BaseTable from "@/components/BaseTable"
import BaseModal from "@/components/BaseModal"
import Utilities from "@/assets/js/utilities.js"
import LearnerResultsSummary from "@/components/LearnerResultsSummary"

export default {
    name: "LearnerDetail",
    components: {
        BaseTable,
        BaseModal,
        LearnerResultsSummary,
    },
    data() {
        return {
            learnerUser: {
                sessions: [],
                lms_instance: {},
            },
            session: {
                activities: [],
                results: {
                    responses: [],
                    timers: [],
                },
            },
            modalLoading: false,
            sessionTableConfig,
        }
    },
    computed: {
        sessionResponsesEmpty() {
            let filteredResponses = this.session.results.responses.filter(
                (r) => r.scored_choices.length > 0 || r.text_responses.length > 0
            )
            return filteredResponses.length == 0
        },
        backRoute() {
            return {
                name: "MemberList",
                query: {
                    filter: "learners"
                }
            }
        },
    },
    methods: {
        // fetch data for the view
        fetchData() {
            this.$store.dispatch("addLoad")
            let vm = this
            http.get("/api/learner-users/" + this.$route.params.id + "/").then(function (response) {
                vm.learnerUser = response.data
                vm.$store.dispatch("removeLoad")
            })
        },
        // returns a formatted date string
        displayDate(dateStr) {
            if (!dateStr) {
                return ""
            }
            return Utilities.formatDateTime(dateStr)
        },
        // open the activity modal
        showActivity(session) {
            $("#activity-modal").modal("show")
            this.loadSession(session)
        },
        // open the text responses modal
        showTextResponses(session) {
            $("#response-modal").modal("show")
            this.loadSession(session)
        },
        // open the submitted choices modal
        showSessionResponses(session) {
            $("#response-modal").modal("show")
            this.loadSession(session)
        },
        downloadSummary(session) {
            window.open(`/export/sessions/${session.id}/response-summary`, "_blank")
        },
        // fetch the selected session unless it's already been loaded
        loadSession(session) {
            if (session.id === this.session.id) {
                return
            }
            this.session = {
                activities: [],
                results: {
                    responses: [],
                    timers: [],
                },
            }
            this.session.activities = []
            this.modalLoading = true
            let vm = this
            http.get("/api/sessions/" + session.id + "/").then(function (response) {
                vm.session = response.data
                vm.modalLoading = false
            })
        },
        panelIconType(panelType) {
            let iconTypes = {
                multiselect: "icon-glyphicons-basic-square-empty-check",
                singleselect: "icon-glyphicons-basic-circle-selected",
                multichoice: "icon-glyphicons-basic-directions-arrow",
                step_order: "icon-glyphicons-basic-sort-numerically",
                textarea: "icon-glyphicons-basic-paragraph-left",
                chat: "icon-glyphicons-basic-chat-conversation",
                image_hotspots: "icon-glyphicons-basic-target",
                categories: "icon-glyphicons-basic-block-move",
            }
            return iconTypes[panelType]
        },
        navigateToNode(node) {
            $("#response-modal").modal("hide")
            this.$router.push({
                name: "NodeDetail",
                params: {
                    nodeID: node.id,
                    id: node.case_id,
                },
            })
        },
    },
    created() {
        this.fetchData()
    },
    mounted() {
        // Make sure when the modal is closed, the content is removed. This stops audio files
        // and videos from playing the background after the modal is closed.
        let vm = this
        $("#response-modal").on("hidden.bs.modal", function (event) {
            vm.session = {
                activities: [],
                results: {
                    responses: [],
                    timers: [],
                },
            }
        })
    },
}

const sessionTableConfig = {
    initialSort: "Started On",
    initialSortDescend: true,
    multiSelect: false,
    actions: true,
    customActions: [
        {
            name: "Activity",
            event: "activity",
        },
        {
            name: "Responses",
            event: "responses",
        },
        {
            name: "Export Summary",
            event: "download",
        },
    ],
    columns: [
        {
            name: "Learning Experience",
            sort: true,
            key: "case.title",
            link: (row) => {
                return `/library/${row.case.id}/settings/`
            },
        },
        {
            name: "Complete",
            sort: true,
            key: "complete",
            display: (row) => {
                return row.complete ? "Yes" : "No"
            },
        },
        {
            name: "Successful",
            sort: true,
            key: "successful",
            display: (row) => {
                return row.successful ? "Yes" : "No"
            },
        },
        {
            name: "Score",
            sort: true,
            key: "score",
            display: (row) => {
                if (row.score && row.score_max) {
                    return row.score + " / " + row.score_max
                } else if (row.score !== null) {
                    return String(row.score)
                } else {
                    return "-"
                }
            },
        },
        {
            name: "Started On",
            sort: true,
            type: "date",
            sortKey: "start_time",
            display: (row) => {
                return Utilities.formatDateTime(row.start_time)
            },
        },
    ],
}
</script>
